import { createFileRoute, redirect } from '@tanstack/react-router';
import { Download, FileText, Loader2, Receipt, ScrollText, Star } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Card, CardContent } from '@/components/atoms/card';
import { formatDateOnly } from '@/lib/dates/date-only';
import { hasLivePortalSession } from '@/stores/portalAuth.store';
import { usePortalDocuments, type PortalDocument } from '@/hooks/portal/usePortalDocuments';
import { PortalShell } from '@/components/portal/PortalShell';

export const Route = createFileRoute('/(portal)/portal/documents/')({
  beforeLoad: () => {
    if (!hasLivePortalSession()) {
      throw redirect({ to: '/portal/login' });
    }
  },
  component: PortalDocumentsPage,
});

const KIND_ICON = {
  estimate: FileText,
  invoice: Receipt,
  membership: Star,
  report: ScrollText,
} as const;

const KIND_LABEL = {
  estimate: 'Estimate',
  invoice: 'Invoice',
  membership: 'Membership',
  report: 'Visit report',
} as const;

function PortalDocumentsPage() {
  const documents = usePortalDocuments();

  return (
    <PortalShell>
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Documents</h1>
        <p className="text-sm text-muted-foreground">
          Estimates, invoices, membership agreements and visit reports — all in one place.
        </p>
      </div>

      {documents.isPending ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : documents.isError ? (
        <Card>
          <CardContent className="space-y-3 py-6 text-sm text-muted-foreground">
            <p>We couldn&apos;t load your documents. Please try again.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void documents.refetch()}
              disabled={documents.isFetching}
            >
              {documents.isFetching ? <Loader2 className="size-4 animate-spin" /> : null}
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : documents.data.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            You don&apos;t have any documents yet.
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="divide-y divide-border">
            {documents.data.map((d) => (
              <DocumentRow key={`${d.kind}-${d.id}`} document={d} />
            ))}
          </div>
        </Card>
      )}
    </PortalShell>
  );
}

function DocumentRow({ document }: { document: PortalDocument }) {
  const Icon = KIND_ICON[document.kind];
  return (
    <a
      href={document.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-muted/50"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{document.title}</p>
        <p className="truncate text-xs text-muted-foreground">
          {KIND_LABEL[document.kind]}
          {document.date ? ` · ${formatDateOnly(document.date)}` : ''}
        </p>
      </div>
      <Download className="size-4 shrink-0 text-muted-foreground" />
    </a>
  );
}
